import { createId, nowIso } from '../lib/ids';
import {
  CURRENT_SCHEMA_VERSION,
  INSTRUMENTS,
  isDrum,
  type AudioBus,
  type Layer,
  type Instrument,
  type NoteEvent,
  type Pattern,
  type Project,
  type Scene,
  type SceneElement,
  type Section,
  type SectionKind,
  type Track,
  type TrackEffects,
} from './schemas';

export const INSTRUMENT_LABELS: Record<Instrument, string> = {
  kick: 'Kick',
  snare: 'Snare',
  clap: 'Clap',
  hats: 'Hi-hats',
  perc: 'Percussion',
  bass808: '808 bass',
  cowbell: 'Cowbell',
  lead: 'Synth lead',
  pad: 'Pad',
};

/** Default MIDI pitch used when a note is added to an instrument (drums use it as tuning). */
export const DEFAULT_PITCH: Record<Instrument, number> = {
  kick: 24,
  snare: 50,
  clap: 50,
  hats: 66,
  perc: 50,
  bass808: 28,
  cowbell: 73,
  lead: 64,
  pad: 57,
};

/** Playable pitch range per instrument (inclusive MIDI numbers). */
export const PITCH_RANGE: Record<Instrument, [number, number]> = {
  kick: [18, 36],
  snare: [40, 60],
  clap: [40, 60],
  hats: [55, 80],
  perc: [36, 72],
  bass808: [21, 45],
  cowbell: [62, 90],
  lead: [52, 88],
  pad: [43, 79],
};

const EFFECTS = (patch: Partial<TrackEffects> = {}): TrackEffects => ({
  drive: 0,
  cutoffHz: 20000,
  delaySend: 0,
  reverbSend: 0,
  ...patch,
});

const TRACK_DEFAULTS: Record<Instrument, Pick<Track, 'volumeDb' | 'pan' | 'effects'>> = {
  kick: { volumeDb: 0, pan: 0, effects: EFFECTS() },
  snare: { volumeDb: -4, pan: 0, effects: EFFECTS({ reverbSend: 0.15 }) },
  clap: { volumeDb: -5, pan: 0, effects: EFFECTS({ reverbSend: 0.2 }) },
  hats: { volumeDb: -10, pan: 0.15, effects: EFFECTS() },
  perc: { volumeDb: -9, pan: -0.25, effects: EFFECTS({ reverbSend: 0.1 }) },
  bass808: { volumeDb: -4, pan: 0, effects: EFFECTS({ drive: 0.2 }) },
  cowbell: { volumeDb: -9, pan: 0.1, effects: EFFECTS({ cutoffHz: 9000, delaySend: 0.12, reverbSend: 0.15 }) },
  lead: { volumeDb: -11, pan: -0.1, effects: EFFECTS({ cutoffHz: 7000, delaySend: 0.2, reverbSend: 0.25 }) },
  pad: { volumeDb: -12, pan: 0, effects: EFFECTS({ cutoffHz: 3500, reverbSend: 0.4 }) },
};

export function createTrack(instrument: Instrument, patch: Partial<Track> = {}): Track {
  return {
    id: createId('trk'),
    name: INSTRUMENT_LABELS[instrument],
    instrument,
    muted: false,
    solo: false,
    sample: null,
    ...structuredClone(TRACK_DEFAULTS[instrument]),
    ...patch,
  };
}

export function defaultTrackSettings(instrument: Instrument): Pick<Track, 'volumeDb' | 'pan' | 'effects'> {
  return structuredClone(TRACK_DEFAULTS[instrument]);
}

export function note(step: number, pitch: number, velocity = 0.9, length = 1, offset = 0): NoteEvent {
  return { step, pitch, velocity, length, offset };
}

/**
 * The built-in demo groove: the original Phase 0 drum & bass loop (E minor), now on the
 * multi-instrument engine.
 */
export function demoPattern(id = createId('pat'), name = 'Demo groove'): Pattern {
  const hits = (steps: Record<number, number>, instrument: Instrument) =>
    Object.entries(steps).map(([s, v]) => note(Number(s), DEFAULT_PITCH[instrument], v));
  return {
    id,
    name,
    lengthSteps: 16,
    tracks: {
      kick: hits({ 0: 1, 6: 0.85, 8: 1, 11: 0.7 }, 'kick'),
      snare: hits({ 4: 1, 12: 1, 15: 0.35 }, 'snare'),
      hats: hits({ 0: 0.6, 2: 0.45, 4: 0.6, 6: 0.45, 7: 0.3, 8: 0.6, 10: 0.45, 12: 0.6, 13: 0.3, 15: 0.5 }, 'hats'),
      bass808: [
        note(0, 28, 1, 1),
        note(3, 28, 0.8, 1),
        note(6, 31, 0.9, 1),
        note(8, 28, 1, 1),
        note(10, 38, 0.85, 1),
        note(11, 35, 0.7, 1),
        note(14, 33, 0.8, 1),
      ],
    },
  };
}

export function emptyPattern(name = 'New pattern', lengthSteps: Pattern['lengthSteps'] = 16): Pattern {
  return { id: createId('pat'), name, lengthSteps, tracks: {} };
}

export const SECTION_LABELS: Record<SectionKind, string> = {
  intro: 'Intro',
  verse: 'Verse',
  buildup: 'Buildup',
  drop: 'Drop',
  breakdown: 'Breakdown',
  outro: 'Outro',
};

export function createSection(kind: SectionKind, patternId: string, bars: number, name = SECTION_LABELS[kind]): Section {
  return { id: createId('sec'), name, kind, patternId, bars };
}

export function createSceneElement(partial: Partial<SceneElement> = {}): SceneElement {
  return {
    id: createId('el'),
    shape: 'circle',
    color: '#a78bfa',
    x: 0.5,
    y: 0.5,
    size: 0.08,
    motion: 'orbit',
    phase: 0,
    ...partial,
  };
}

export const DEFAULT_SCENE_WIDTH = 1280;
export const DEFAULT_SCENE_HEIGHT = 720;

export function createLayer(name = 'Layer 1', id = createId('lyr')): Layer {
  return { id, name, visible: true, locked: false };
}

/** Phase 3A editor fields for a scene (one empty layer, 1280×720, plain background). */
export function sceneEditorDefaults(layerId: string = createId('lyr')): Pick<Scene, 'width' | 'height' | 'gradient' | 'backgroundImage' | 'layers' | 'objects'> {
  return {
    width: DEFAULT_SCENE_WIDTH,
    height: DEFAULT_SCENE_HEIGHT,
    gradient: null,
    backgroundImage: null,
    layers: [createLayer('Layer 1', layerId)],
    objects: [],
  };
}

export function createDefaultScene(): Scene {
  return {
    id: createId('scn'),
    name: 'Main scene',
    background: '#0b0b12',
    speed: 1,
    ...sceneEditorDefaults(),
    elements: [
      createSceneElement({ shape: 'circle', color: '#a78bfa', x: 0.5, y: 0.5, size: 0.1, motion: 'orbit', phase: 0 }),
      createSceneElement({ shape: 'square', color: '#22d3ee', x: 0.3, y: 0.4, size: 0.07, motion: 'spin', phase: 0.25 }),
      createSceneElement({ shape: 'triangle', color: '#f472b6', x: 0.7, y: 0.6, size: 0.08, motion: 'bounce', phase: 0.5 }),
      createSceneElement({ shape: 'circle', color: '#facc15', x: 0.5, y: 0.25, size: 0.05, motion: 'pulse', phase: 0.75 }),
    ],
  };
}

export function createProject(name: string, description = ''): Project {
  const now = nowIso();
  const scene = createDefaultScene();
  const pattern = demoPattern();
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: createId('prj'),
    name: name.trim(),
    description,
    createdAt: now,
    updatedAt: now,
    settings: { bpm: 96, swing: 0, masterVolumeDb: -6, key: { root: 4, scale: 'minor' } },
    transport: { mode: 'pattern', loopSong: false },
    tracks: INSTRUMENTS.map((instrument) => createTrack(instrument)),
    patterns: [pattern],
    activePatternId: pattern.id,
    arrangement: { sections: [createSection('verse', pattern.id, 4, 'Loop')] },
    generator: null,
    scenes: [scene],
    activeSceneId: scene.id,
    assets: [],
    generationRequests: [],
    outputs: [],
    audioClips: [],
    audioBus: defaultAudioBus(),
    characters: [],
    animations: [],
    avSessions: [],
  };
}

export function defaultAudioBus(): AudioBus {
  return { volumeDb: -3, pan: 0, muted: false, solo: false, delaySend: 0, reverbSend: 0 };
}

/** Returns the scene currently selected in the project (validated to exist by the schema). */
export function activeScene(project: Project): Scene {
  return project.scenes.find((s) => s.id === project.activeSceneId) ?? project.scenes[0]!;
}

export function activePattern(project: Project): Pattern {
  return project.patterns.find((p) => p.id === project.activePatternId) ?? project.patterns[0]!;
}

export function trackFor(project: Project, instrument: Instrument): Track | undefined {
  return project.tracks.find((t) => t.instrument === instrument);
}

/** Notes of one instrument in a pattern (empty array when none). */
export function notesFor(pattern: Pattern, instrument: Instrument): readonly NoteEvent[] {
  return pattern.tracks[instrument] ?? [];
}

export const defaultNoteLength = (instrument: Instrument): number => (isDrum(instrument) ? 1 : instrument === 'pad' ? 8 : 2);
